import {
    QueryKey,
    useQueries,
    useQuery,
    UseQueryResult,
} from "@tanstack/react-query";
import { useApiClient } from "../providers/ApiClientProvider";

interface UseApiQueryOptions<T> {
    queryKey: QueryKey;
    endpoint: string;
    enabled?: boolean;
    select?: (data: T) => any;
}

export function useApiQuery<T>({
    queryKey,
    endpoint,
    enabled = true,
    select,
}: UseApiQueryOptions<T>) {
    const apiClient = useApiClient();

    return useQuery<T, Error>({
        queryKey,
        queryFn: async ({ signal }) => {
            const response = await apiClient.get<{ data: T }>(endpoint, {
                signal,
            });
            return response.data;
        },
        enabled,
        select,
    });
}

interface UseApiQueriesOptions<T> {
    queries: UseApiQueryOptions<T>[];
    combine?: (results: UseQueryResult<T>[]) => any;
}

export function useApiQueries<T>({
    queries,
    combine,
}: UseApiQueriesOptions<T>) {
    const apiClient = useApiClient();

    return useQueries({
        queries: queries.map(({ queryKey, endpoint, enabled, select }) => ({
            queryKey,
            queryFn: async ({ signal }) => {
                const response = await apiClient.get<{ data: T }>(endpoint, {
                    signal,
                });
                return response.data;
            },
            enabled,
            select,
        })),
        combine,
    });
}
